import { useIntl } from 'react-intl';
import { useEffect, useMemo } from 'react';

import { fg } from '@confluence/feature-gating';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import type {
	ForgeUIExtensionQueryOptions,
	UsePreFilteredExtensionsQueryOptions,
} from '@atlassian/forge-ui/provider';
import {
	usePrefilteredExtensionList as usePrefilteredExtensionListApollo,
	usePrefilteredExtensionListRelay,
} from '@atlassian/forge-ui/provider';

import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { getUsePrefilteredExtensionListOptions } from './getUsePrefilteredExtensionListOptions.ts';

export type UseExtensionListProps = {
	moduleType: string;
	queryOptions?: UsePreFilteredExtensionsQueryOptions | ForgeUIExtensionQueryOptions;
	returnBlockedExtensions?: boolean;
	skipQuery?: boolean;
	spaceContextOnly?: boolean;
};

export const useExtensionList = ({
	moduleType,
	queryOptions,
	returnBlockedExtensions = false,
	skipQuery = false,
	spaceContextOnly,
}: UseExtensionListProps) => {
	const { locale } = useIntl();
	const { cloudId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { error: spaceError } = useSpaceDetail(spaceKey);

	const options = useMemo(
		() =>
			getUsePrefilteredExtensionListOptions({
				cloudId,
				locale,
				moduleType,
				queryOptions: {
					...queryOptions,
					skip: skipQuery,
				},
				includeHidden: returnBlockedExtensions,
				spaceKey,
				contentId,
				spaceContextOnly,
			}),
		[
			cloudId,
			locale,
			moduleType,
			queryOptions,
			returnBlockedExtensions,
			skipQuery,
			spaceKey,
			contentId,
			spaceContextOnly,
		],
	);

	const {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	} = usePrefilteredExtensionList(options);

	useEffect(() => {
		// suppress 403 errors as we can't do much with them
		if (extsError && isUnauthorizedError(extsError)) {
			markErrorAsHandled(extsError);
		}

		// suppress 429 errors for offending users
		if (isTooManyRequestsError(extsError)) {
			markErrorAsHandled(extsError);
		}

		if (spaceError) {
			markErrorAsHandled(spaceError);
		}
	}, [extsError, spaceError]);

	return {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	};
};

const usePrefilteredExtensionList = conditionalHooksFactory(
	() => fg('forge-ui-migrate-extension-list-query-to-relay'),
	usePrefilteredExtensionListRelay,
	usePrefilteredExtensionListApollo,
);
